// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerFormGroup, DealTypeInputs } from '@interfaces/create-deal-inputs.interface';
import { Deal, SelectedDealType } from '@interfaces/deal.interfaces';
import { Assignees, TemplateGroup } from '@interfaces/document.interfaces';
import { AppEvent } from '@interfaces/interfaces';
import { User, UserTypes } from '@interfaces/user.interfaces';
import { TradeInVehicle, Vehicle } from '@interfaces/vehicle.interfaces';
import { DealsService } from '@services/deals.service';
import { LeadsService } from '@services/leads.service';
import { NavService } from '@services/nav.service';
import { UserService } from '@services/user.service';
import { VehicleService } from '@services/vehicle.service';
import { catchError, debounceTime, distinctUntilChanged, firstValueFrom, Subscription, takeWhile, of } from 'rxjs';
import { DealCreatedModalComponent } from 'src/app/modals/deal-created-modal/deal-created-modal.component';
import { VehicleSearchModal } from 'src/app/modals/vehicle-search-modal/vehicle-search-modal.component';
import { namePattern, phoneExporter, phoneImporter, showFormErrors, usPhonePattern, emailPattern } from 'src/app/utils/form-utils';
import { scrollIntoView } from 'src/app/utils/utils';
import { Buffer } from 'buffer';
import { MatRadioChange } from '@angular/material/radio';
import { FeatureFlagService } from '@services/feature-flag.service';
import { formatPhone } from 'src/app/utils/format-util';

@Component({
    selector: 'start-deal-jacket',
    templateUrl: './start-deal-jacket.component.html',
    styleUrls: ['./start-deal-jacket.component.scss'],
})
export class StartDealJacketComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('dealType') dealTypeRef: ElementRef;
    @ViewChild('helpPanel') helpPanel: TemplateRef<any>;

    public leadId: number;
    public createError: string;
    public existingDocumentJacketDeal: Deal = null;
    public exisitingDocumentJacketDealId?: number = null;
    public buyerFound = false;
    public buyerLogonId: string = null;
    private buyerOriginalPhone: string = null;
    public coBuyerFound = false;
    private coBuyerInfoAdded = false;
    private coBuyerInfoCleared = false;
    public coBuyerLogonId: string = null;
    private coBuyerOriginalPhone: string = null;
    public customTemplateGroups: TemplateGroup[] = [];
    public dealTypeSelections: DealTypeInputs = {
        isNew: true,
        isBuy: true,
        isCash: false,
        isTradeIn: false,
        isLeaseTurnIn: false,
    };
    public duplicateBuyer = false;
    public isBuyerDealer = false;
    public isCoBuyerDealer = false;
    private isComponentAlive = true;
    public isLoading = false;
    public newDeal: FormGroup;
    private obs$: Subscription;
    public SelectedDealType = SelectedDealType; // Used in template
    // Defaults to blank deal as express upload creates a blank deal to start
    public selectedDealType: SelectedDealType = SelectedDealType.Express;
    public selectedTemplateGroup: TemplateGroup = {
        groupName: this.selectedDealType.toString(),
        templateGroupId: null,
        templates: [],
    };
    public selectGroup = false;
    private selectedVehicle: Vehicle = null;
    public expandTradeIn = false;
    public carStatus: { value: string; displayValue: string }[] = [
        { value: 'new', displayValue: 'New' },
        { value: 'used', displayValue: 'Used' },
    ];

    private defaultToMfa = false;

    constructor(
        private dealsService: DealsService,
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private readonly dialog: MatDialog,
        private readonly navService: NavService,
        private readonly userService: UserService,
        private leadsService: LeadsService,
        private vehicleService: VehicleService,

        private readonly featureFlag: FeatureFlagService
    ) {
        this.defaultToMfa = this.featureFlag.getDefaultFeatureConfiguration('defaultToMfa'); // TODO: Consider enhancing feature flag service to make flags global constants
    }

    async ngOnInit(): Promise<void> {
        //Setting to null in the instance that a document jacket is getting passed in and isn't refreshing
        this.dealsService.setCurrentDealId(null);
        this.customTemplateGroups = this.route.snapshot.data.groups;
        this.buildForm();
        if (this.route.snapshot.queryParams.leadId) {
            this.leadId = parseInt(this.route.snapshot.queryParams.leadId);
        }
        this.navService.setTitle('Start Deal Jacket');
        if (this.leadId) {
            // prepopulate form with lead info
            this.getLeadDetails(this.leadId);
        }

        if (this.route.snapshot.queryParams.documentJacketDeal) {
            this.existingDocumentJacketDeal = JSON.parse(Buffer.from(this.route.snapshot.queryParams.documentJacketDeal, 'base64').toString('ascii'));
            this.preLoadDocumentJacketDeal(this.existingDocumentJacketDeal);
        }

        this.newDeal.controls.buyerEmail.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500)
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.newDeal.controls.buyerEmail.setValue(value.trim());
            });

        this.newDeal.controls.coBuyerGroup
            .get('coBuyerEmail')
            .valueChanges.pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500)
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.newDeal.controls.coBuyerGroup.get('coBuyerEmail').setValue(value.trim());
            });
    }

    ngAfterViewInit(): void {
        this.navService.setHelpPanel(this.helpPanel);
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
        this.navService.setHelpPanel(null);

        if (this.obs$) {
            this.obs$.unsubscribe();
        }
    }

    private buildForm(): void {
        this.newDeal = this.fb.group({
            dealerDealNumber: [null, Validators.maxLength(20)],
            buyerEmail: ['', Validators.compose([Validators.required, Validators.maxLength(500), Validators.pattern(emailPattern)])],
            buyerPhone: ['', Validators.compose([Validators.required, Validators.pattern(usPhonePattern)])],
            buyerFirstName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            buyerMiddleName: ['', Validators.compose([Validators.maxLength(128), Validators.pattern(namePattern)])],
            buyerLastName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            buyerEnableMFA: [null],
            coBuyerGroup: this.fb.group({
                coBuyerEmail: [''],
                coBuyerPhone: [''],
                coBuyerFirstName: [''],
                coBuyerMiddleName: [''],
                coBuyerLastName: [''],
                coBuyerEnableMFA: [null],
            }),
            tradeInGroup: this.fb.group({
                vin: ['', Validators.compose([Validators.minLength(5), Validators.maxLength(18)])],
                bodyStyle: ['', Validators.compose([Validators.maxLength(128)])],
                bodyType: [''],
                extColor: [''],
                intColor: [''],
                make: [''],
                model: [''],
                payoff: [''],
                stockNumber: [''],
                priceInternet: [null],
                year: [null, Validators.compose([Validators.min(1900), Validators.max(3000)])],
                trim: [''],
                mileage: [null],
            }),
            vehicleGroup: this.fb.group({
                vin: ['', Validators.compose([Validators.minLength(5), Validators.maxLength(18)])],
                stockNumber: [''],
                priceInternet: [null],
                status: [null],
                year: [null, Validators.compose([Validators.min(1900), Validators.max(3000)])],
                make: [''],
                model: [''],
                trim: [''],
                bodyType: [''],
                bodyStyle: [''],
                extColor: [''],
                intColor: [''],
                mileage: [null],
            }),
        });

        this.coBuyerChanges();
    }

    private async getLeadDetails(leadId: number) {
        this.isLoading = true;

        const lead = await firstValueFrom(this.leadsService.getLead$(this.userService.getDealerId(), leadId));

        if (lead) {
            if (lead.dealId && lead.leadScreenStatus.includes('Document Jacket')) {
                this.exisitingDocumentJacketDealId = lead.dealId;

                // get the deal jacket
                this.dealsService
                    .getDeal$(lead.dealId)
                    .pipe(takeWhile(() => this.isComponentAlive))
                    .subscribe((jacket) => {
                        this.existingDocumentJacketDeal = jacket;
                    });
            }

            if (lead.tradeInVin) {
                this.expandTradeIn = true;
            }

            const res = await firstValueFrom(this.vehicleService.searchForVehicle$(lead.vin));

            if (res && res.length > 0) {
                const found = res.find((v) => v.vin == lead.vin);
                if (found && found.vin == lead.vin) {
                    this.selectedVehicle = found;
                }
            }

            this.newDeal.patchValue({
                buyerEmail: lead.buyerEmail,
                buyerPhone: lead.buyerPhone,
                buyerFirstName: lead.buyerFirstName,
                // buyerMiddleName: lead.buyerMiddleName,
                buyerLastName: lead.buyerLastName,
                vehicleGroup: {
                    vin: this.selectedVehicle ? this.selectedVehicle.vin : lead.vin,
                    stockNumber: this.selectedVehicle ? this.selectedVehicle.stockNumber : lead.stockNumber,
                    priceInternet: this.selectedVehicle
                        ? this.selectedVehicle.vehicleExt?.priceInternet
                        : lead.price !== ''
                          ? lead?.price?.replace(/[a-z$,.]/g, '')
                          : null,
                    status: this.selectedVehicle
                        ? this.carStatus.find((status) => status.value === (this.selectedVehicle?.vehicleExt?.status || '').toLowerCase())?.value
                        : this.carStatus.find((status) => status.value === (lead?.status || '').toLowerCase())?.value,
                    year: this.selectedVehicle ? this.selectedVehicle.year : lead.year,
                    make: this.selectedVehicle ? this.selectedVehicle.make : lead.make,
                    model: this.selectedVehicle ? this.selectedVehicle.model : lead.model,
                    trim: this.selectedVehicle ? this.selectedVehicle.trim : lead.trim,
                    bodyType: this.selectedVehicle?.vehicleExt?.bodyType ?? '',
                    bodyStyle: this.selectedVehicle?.bodyStyle ?? '',
                    extColor: this.selectedVehicle?.vehicleExt?.exteriorColor ?? '',
                    intColor: this.selectedVehicle?.vehicleExt?.interiorColor ?? '',
                    mileage: this.selectedVehicle?.mileage ?? null,
                },
                tradeInGroup: {
                    vin: lead.tradeInVin,
                    bodyStyle: lead.tradeInBodyStyle,
                    year: lead.tradeInYear,
                    make: lead.tradeInMake,
                    model: lead.tradeInModel,
                    trim: lead.tradeInTrim,
                    priceInternet: lead.tradeInPrice !== '' ? (lead?.tradeInValue || '').replace(/[a-z$,.]/g, '') : null,
                    payoff: lead.tradeInPayoff,
                },
            });

            // If buyer email is present make sure to check if buyer
            // is already in the system

            if (lead.buyerEmail) {
                this.findUser(Assignees.buyer, lead);
            }
        }

        this.isLoading = false;
    }

    private preLoadDocumentJacketDeal(preDealJacket: Deal) {
        this.exisitingDocumentJacketDealId = preDealJacket.dealId;
        this.newDeal.patchValue({
            buyerEmail: preDealJacket.buyer.email,
            buyerPhone: preDealJacket.buyer.phone,
            buyerFirstName: preDealJacket.buyer.firstName,
            // buyerMiddleName: preDealJacket.buyer.middleName,
            buyerLastName: preDealJacket.buyer.lastName,
            buyerEnableMFA: preDealJacket.buyer.enableMFA,
            vehicleGroup: {
                vin: this.selectedVehicle ? this.selectedVehicle.vin : preDealJacket.vehicle.vin,
                stockNumber: this.selectedVehicle ? this.selectedVehicle.stockNumber : preDealJacket.vehicle.stockNumber,
                priceInternet: this.selectedVehicle ? this.selectedVehicle.vehicleExt.priceInternet : preDealJacket.vehicle.vehicleExt.priceInternet,
                status: this.selectedVehicle
                    ? this.carStatus.find((status) => status.value === (this.selectedVehicle.vehicleExt?.status || '').toLowerCase())?.value
                    : this.carStatus.find((status) => status.value === (preDealJacket.vehicle.vehicleExt?.status || '').toLowerCase())?.value,
                year: this.selectedVehicle ? this.selectedVehicle.year : preDealJacket.vehicle.year,
                make: this.selectedVehicle ? this.selectedVehicle.make : preDealJacket.vehicle.make,
                model: this.selectedVehicle ? this.selectedVehicle.model : preDealJacket.vehicle.model,
                trim: this.selectedVehicle ? this.selectedVehicle.trim : preDealJacket.vehicle.trim,
                bodyType: this.selectedVehicle?.vehicleExt?.bodyType ?? preDealJacket.vehicle.vehicleExt?.bodyType,
                bodyStyle: this.selectedVehicle?.bodyStyle ?? preDealJacket.vehicle.bodyStyle,
                extColor: this.selectedVehicle?.vehicleExt?.exteriorColor ?? preDealJacket.vehicle.vehicleExt?.exteriorColor,
                intColor: this.selectedVehicle?.vehicleExt?.interiorColor ?? preDealJacket.vehicle.vehicleExt?.interiorColor,
                mileage: this.selectedVehicle?.mileage ?? preDealJacket.vehicle.mileage,
            },
            tradeInGroup: {
                vin: preDealJacket.tradeInVehicle?.vin,
                bodyStyle: preDealJacket.tradeInVehicle?.bodyStyle,
                year: preDealJacket.tradeInVehicle?.year,
                make: preDealJacket.tradeInVehicle?.make,
                model: preDealJacket.tradeInVehicle?.model,
                trim: preDealJacket.tradeInVehicle?.trim,
                priceInternet: preDealJacket.tradeInVehicle?.vehicleExt?.priceInternet,
                payoff: preDealJacket.tradeInVehicle?.payoff,
                mileage: preDealJacket.tradeInVehicle?.mileage,
            },
        });

        // Only add the coBuyer if they exist, so we don't screw up any validation handling
        if (preDealJacket.coBuyer.userId) {
            this.newDeal.patchValue({
                coBuyerGroup: {
                    coBuyerEmail: preDealJacket.coBuyer.email,
                    coBuyerPhone: preDealJacket.coBuyer.phone,
                    coBuyerFirstName: preDealJacket.coBuyer.firstName,
                    coBuyerMiddleName: preDealJacket.coBuyer.middleName,
                    coBuyerLastName: preDealJacket.coBuyer.lastName,
                    coBuyerEnableMFA: preDealJacket.coBuyer.enableMFA,
                },
            });
        }

        // If buyer email is present make sure to check if buyer
        // is already in the system
        if (preDealJacket.buyer.email) {
            this.findUser(Assignees.buyer, null, true);
        }
        // same with cobuyer
        if (preDealJacket.coBuyer.email) {
            this.findUser(Assignees.cobuyer, null, true);
        }
    }

    private coBuyerChanges(): void {
        this.newDeal
            .get('coBuyerGroup')
            .valueChanges.pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(250),
                distinctUntilChanged()
            )
            .subscribe((val) => {
                const formChanged = Object.values(val).filter((val) => val !== '');

                if (!this.coBuyerInfoAdded && formChanged.length > 0) {
                    this.setCoBuyerValidators(true);
                    return;
                }

                if (val.coBuyerEmail === '') {
                    this.setCoBuyerValidators(false);
                } else {
                    this.setCoBuyerValidators(true);
                }

                if (!this.coBuyerInfoCleared && formChanged.length <= 0) {
                    this.setCoBuyerValidators(false);
                    return;
                }
            });
    }

    customerLogon(logonId: any) {
        this.buyerLogonId = logonId;
    }

    coBuyerLogon(logonId: any) {
        this.coBuyerLogonId = logonId;
    }

    public eventHandler(event: AppEvent): void {
        this.selectGroup = false;
        this.selectedTemplateGroup = event.data;
    }

    enableCoBuyerFormGroup(enable: boolean): void {
        const formGroup = this.newDeal.controls['coBuyerGroup'] as FormGroup;
        if (enable) {
            formGroup.controls.coBuyerFirstName.enable();
            formGroup.controls.coBuyerLastName.enable();
            formGroup.controls.coBuyerMiddleName.enable();
            formGroup.controls.coBuyerPhone.enable();
        } else {
            formGroup.controls.coBuyerFirstName.disable();
            formGroup.controls.coBuyerLastName.disable();
            formGroup.controls.coBuyerMiddleName.disable();
            formGroup.controls.coBuyerPhone.disable();
        }
    }

    enableBuyerFormGroup(enable: boolean): void {
        if (enable) {
            this.newDeal.controls.buyerFirstName.enable();
            this.newDeal.controls.buyerMiddleName.enable();
            this.newDeal.controls.buyerLastName.enable();
            this.newDeal.controls.buyerPhone.enable();
        } else {
            this.newDeal.controls.buyerFirstName.disable();
            this.newDeal.controls.buyerMiddleName.disable();
            this.newDeal.controls.buyerLastName.disable();
            this.newDeal.controls.buyerPhone.disable();
        }
    }

    resetCoBuyerFormGroup(): void {
        const formGroup = this.newDeal.controls['coBuyerGroup'] as FormGroup;
        formGroup.controls.coBuyerFirstName.patchValue('');
        formGroup.controls.coBuyerLastName.patchValue('');
        formGroup.controls.coBuyerMiddleName.patchValue('');
        formGroup.controls.coBuyerPhone.patchValue('');
    }

    resetBuyerFormGroup(): void {
        this.newDeal.controls.buyerFirstName.patchValue('');
        this.newDeal.controls.buyerMiddleName.patchValue('');
        this.newDeal.controls.buyerLastName.patchValue('');
        this.newDeal.controls.buyerPhone.patchValue('');
    }

    clearBuyerFormGroup(e: any, type: 'buyer' | 'cobuyer'): void {
        e.preventDefault();

        if (type === 'buyer') {
            this.newDeal.controls.buyerEmail.patchValue('');
            this.newDeal.controls.buyerFirstName.patchValue('');
            this.newDeal.controls.buyerMiddleName.patchValue('');
            this.newDeal.controls.buyerLastName.patchValue('');
            this.newDeal.controls.buyerPhone.patchValue('');

            if (this.buyerFound === true) {
                this.enableBuyerFormGroup(true);
            }

            this.buyerFound = false;
            this.buyerOriginalPhone = undefined;
            this.buyerLogonId = undefined;
            this.isBuyerDealer = false;
            this.duplicateBuyer = false;
        } else {
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail.patchValue('');
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerFirstName.patchValue('');
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerMiddleName.patchValue('');
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerLastName.patchValue('');
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerPhone.patchValue('');

            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail.setErrors(null);
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerFirstName.setErrors(null);
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerMiddleName.setErrors(null);
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerLastName.setErrors(null);
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerPhone.setErrors(null);

            if (this.coBuyerFound === true) {
                this.enableCoBuyerFormGroup(true);
            }

            this.coBuyerFound = false;
            this.coBuyerOriginalPhone = undefined;
            this.coBuyerLogonId = undefined;
            this.isCoBuyerDealer = false;
            this.duplicateBuyer = false;
        }
    }

    emptyOrInvalidEmailBlocksRequestResetsForm = (userType: string) => {
        if (userType === Assignees.buyer) {
            if (!this.newDeal.controls.buyerEmail.value && this.buyerFound) {
                this.enableBuyerFormGroup(true);
                this.resetBuyerFormGroup();

                this.buyerFound = false;
                this.buyerOriginalPhone = undefined;
                this.buyerLogonId = undefined;
                this.isBuyerDealer = false;
                this.duplicateBuyer = false;

                return true;
            } else if (!this.newDeal.controls['buyerEmail'].valid) {
                return true;
            }
        } else {
            if (!(this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail.value && this.coBuyerFound) {
                this.enableCoBuyerFormGroup(true);
                this.resetCoBuyerFormGroup();

                this.coBuyerFound = false;
                this.coBuyerOriginalPhone = undefined;
                this.coBuyerLogonId = undefined;
                this.isCoBuyerDealer = false;
                this.duplicateBuyer = false;

                (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail.setErrors(null);

                (this.newDeal.controls['coBuyerGroup'] as FormGroup).clearValidators();
                (this.newDeal.controls['coBuyerGroup'] as FormGroup).updateValueAndValidity();

                return true;
            } else if (!(this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail.valid) {
                return true;
            }
        }
        return false;
    };

    handleFoundBuyer = (foundUser: User, phone: string) => {
        this.buyerOriginalPhone = foundUser.phone;
        this.buyerLogonId = foundUser.logonId;
        this.buyerFound = true;
        this.isBuyerDealer = this.buyerFound && foundUser.userType !== UserTypes.Customer;

        // Disable all but the email so that it can be changed if needed. This has to happen before patching so the phone mask doesn't break.
        this.enableBuyerFormGroup(false);

        // set form values
        this.newDeal.controls.buyerEmail.patchValue(foundUser.email);
        this.newDeal.controls.buyerPhone.patchValue(`*** *** ${phone.substring(phone.length - 4)}`);
        this.newDeal.controls.buyerFirstName.patchValue(foundUser.firstName);
        this.newDeal.controls.buyerMiddleName.patchValue(foundUser.middleName);
        this.newDeal.controls.buyerLastName.patchValue(foundUser.lastName);
        this.newDeal.controls.buyerEnableMFA.patchValue(foundUser.enableMFA);
    };

    handleFoundCoBuyer = (foundUser: User, phone: string) => {
        this.coBuyerOriginalPhone = foundUser.phone;
        this.coBuyerLogonId = foundUser.logonId;
        this.coBuyerFound = true;
        this.isCoBuyerDealer = this.coBuyerFound && foundUser.userType !== UserTypes.Customer;
        const formGroup = this.newDeal.controls['coBuyerGroup'] as FormGroup;

        // Disable all but the email so that it can be changed if needed. This has to happen before patching so the phone mask doesn't break.
        this.enableCoBuyerFormGroup(false);

        // set form values
        formGroup.controls.coBuyerEmail.patchValue(foundUser.email);
        formGroup.controls.coBuyerPhone.patchValue(`*** *** ${phone.substring(phone.length - 4)}`);
        formGroup.controls.coBuyerFirstName.patchValue(foundUser.firstName);
        formGroup.controls.coBuyerMiddleName.patchValue(foundUser.middleName);
        formGroup.controls.coBuyerLastName.patchValue(foundUser.lastName);
        formGroup.controls.coBuyerEnableMFA.patchValue(foundUser.enableMFA);
    };

    checkForDuplicateBuyerCobuyer = () => {
        // if both are filled out - check if they are dupes
        const buyerEmail = this.newDeal.controls.buyerEmail;
        const coBuyerEmail = (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail;
        if (buyerEmail?.value && coBuyerEmail?.value) {
            if (buyerEmail.value.toLowerCase().trim() === coBuyerEmail.value.toLowerCase().trim()) {
                this.duplicateBuyer = true;
            } else {
                this.duplicateBuyer = false;
            }
        }
    };

    handleNotFoundCobuyer = () => {
        // clear the form if previous cobuyer found

        const formGroup = this.newDeal.controls['coBuyerGroup'] as FormGroup;
        if (this.coBuyerFound) {
            formGroup.controls.coBuyerPhone.patchValue('');
            formGroup.controls.coBuyerFirstName.patchValue('');
            formGroup.controls.coBuyerMiddleName.patchValue('');
            formGroup.controls.coBuyerLastName.patchValue('');
        }

        this.coBuyerFound = false;
        this.coBuyerOriginalPhone = undefined;
        this.coBuyerLogonId = undefined;
        this.isCoBuyerDealer = false;

        // enable inputs
        this.enableCoBuyerFormGroup(true);
    };

    private fillVehicleForm(vehicleInfo: any): void {
        this.selectedVehicle = vehicleInfo;
        //need to pass the dealerId on the vehicle being selected
        this.selectedVehicle.dealerId = this.userService.user.dealerId;

        (this.newDeal.controls['vehicleGroup'] as FormGroup).setValue({
            vin: vehicleInfo.vin,
            stockNumber: vehicleInfo.stockNumber,
            priceInternet: vehicleInfo.vehicleExt.priceInternet,
            status: this.carStatus.find((s) => s.displayValue === vehicleInfo.vehicleExt?.status).value,
            year: vehicleInfo.year,
            make: vehicleInfo.make,
            model: vehicleInfo.model,
            trim: vehicleInfo.trim,
            bodyType: vehicleInfo.vehicleExt.bodyType,
            bodyStyle: vehicleInfo.bodyStyle,
            extColor: vehicleInfo.vehicleExt.exteriorColor,
            intColor: vehicleInfo.vehicleExt.interiorColor,
            mileage: vehicleInfo.mileage,
        });

        (this.newDeal.controls['vehicleGroup'] as FormGroup).updateValueAndValidity();
    }

    findUser(userType: string, lead: any = undefined, isPreDealJacket = false) {
        if (this.emptyOrInvalidEmailBlocksRequestResetsForm(userType)) return;

        this.isLoading = true;

        const searchUser =
            userType === Assignees.buyer
                ? this.newDeal.controls.buyerEmail.value
                : (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail.value;

        this.userService
            .checkUserByEmail$(searchUser)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    throw error;
                })
            )
            .subscribe((foundUser) => {
                if (!foundUser) {
                    this.isLoading = false;
                }

                if (foundUser && foundUser.userId) {
                    foundUser.phone = phoneImporter(foundUser.phone);
                    const phone = foundUser.phone;

                    if (userType === Assignees.buyer) {
                        this.handleFoundBuyer(foundUser, phone);
                        this.isLoading = false;
                    } else {
                        this.handleFoundCoBuyer(foundUser, phone);
                        this.isLoading = false;
                    }
                } else {
                    if (userType === Assignees.buyer && (!lead || !isPreDealJacket)) {
                        // clear the form if previous buyer was found
                        if (this.buyerFound) {
                            this.resetBuyerFormGroup();
                        }

                        this.buyerFound = false;
                        this.buyerOriginalPhone = undefined;
                        this.buyerLogonId = undefined;
                        this.isBuyerDealer = false;

                        // enable inputs
                        this.enableBuyerFormGroup(true);
                    } else if (lead) {
                        this.newDeal.patchValue({
                            buyerPhone: lead.buyerPhone,
                            buyerFirstName: lead.buyerFirstName,
                            buyerLastName: lead.buyerLastName,
                        });
                    } else if (isPreDealJacket) {
                        this.newDeal.patchValue({
                            buyerPhone: this.existingDocumentJacketDeal.buyer.phone,
                            buyerFirstName: this.existingDocumentJacketDeal.buyer.firstName,
                            buyerLastName: this.existingDocumentJacketDeal.buyer.lastName,
                            buyerEnableMFA: this.existingDocumentJacketDeal.buyer.enableMFA,
                        });
                    }

                    if (userType === Assignees.cobuyer) {
                        //log cobuyer not found
                        this.handleNotFoundCobuyer();
                        this.isLoading = false;
                    }
                }

                this.checkForDuplicateBuyerCobuyer();
            });
    }

    private isValid(): boolean {
        let errors = false;

        if (this.newDeal.status === 'INVALID') {
            showFormErrors(this.newDeal.controls);
            if (this.coBuyerInfoAdded) {
                const coBuyerForm = this.newDeal.get('coBuyerGroup') as FormGroup;
                showFormErrors(coBuyerForm.controls);
            }
            scrollIntoView('.form-top');
            this.isLoading = false;
            errors = true;
        }

        if (this.selectedDealType === SelectedDealType.Custom && !this.selectedTemplateGroup) {
            this.selectGroup = true;
            this.isLoading = false;
            errors = true;
        }

        if (errors) {
            return false;
        }

        return true;
    }

    // Determines if selectedVehicle should be used
    // or if manually entered fields should be used as vehicle
    get isVehicleManuallyEntered(): boolean {
        const manualVehicle = this.newDeal.value.vehicleGroup;
        return (
            this.selectedVehicle === null ||
            manualVehicle.vin !== this.selectedVehicle.vin ||
            manualVehicle.stockNumber !== this.selectedVehicle.stockNumber ||
            manualVehicle.priceInternet !== this.selectedVehicle.vehicleExt.priceInternet ||
            manualVehicle.status !== (this.selectedVehicle?.vehicleExt?.status || '').toLowerCase() ||
            manualVehicle.year !== this.selectedVehicle.year ||
            manualVehicle.make.trim() !== this.selectedVehicle.make.trim() ||
            manualVehicle.model !== this.selectedVehicle.model ||
            manualVehicle.trim !== this.selectedVehicle.trim ||
            manualVehicle.bodyStyle !== this.selectedVehicle.bodyStyle ||
            manualVehicle.bodyType !== this.selectedVehicle.vehicleExt.bodyType ||
            manualVehicle.extColor !== this.selectedVehicle.vehicleExt.exteriorColor ||
            manualVehicle.intColor !== this.selectedVehicle.vehicleExt.interiorColor ||
            manualVehicle.mileage !== this.selectedVehicle.mileage
        );
    }

    public openSearch(): void {
        const vehicle = this.dialog.open(VehicleSearchModal);

        this.obs$ = vehicle
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((selectedVehicle) => {
                if (selectedVehicle) {
                    this.selectedVehicle = selectedVehicle;
                    this.fillVehicleForm(selectedVehicle);
                }
            });
    }

    onDealTypeChange(event: MatRadioChange) {
        this.selectedDealType = event.value;
        this.selectGroup = false;
        this.selectedTemplateGroup = null;

        // These groupNames are going to be saved as the dealTypeName if a non-custom deal type is selected
        if (this.selectedDealType === SelectedDealType.Blank || this.selectedDealType === SelectedDealType.Express) {
            this.selectedTemplateGroup = {
                groupName: this.selectedDealType.toString(),
                templateGroupId: null,
                templates: [],
            };
        }
    }

    private setCoBuyerValidators(validate: boolean): void {
        if (validate) {
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail.setValidators([
                Validators.maxLength(500),
                Validators.pattern(emailPattern),
                Validators.required,
            ]);
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail.updateValueAndValidity();

            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerPhone.setValidators([Validators.pattern(usPhonePattern), Validators.required]);
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerPhone.updateValueAndValidity();

            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerFirstName.setValidators([
                Validators.maxLength(128),
                Validators.pattern(namePattern),
                Validators.required,
            ]);
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerFirstName.updateValueAndValidity();

            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerLastName.setValidators([
                Validators.maxLength(128),
                Validators.pattern(namePattern),
                Validators.required,
            ]);
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerLastName.updateValueAndValidity();
            this.coBuyerInfoAdded = true;
        } else {
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail.clearValidators();
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail.updateValueAndValidity();
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerPhone.clearValidators();
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerPhone.updateValueAndValidity();
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerFirstName.clearValidators();
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerFirstName.updateValueAndValidity();
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerLastName.clearValidators();
            (this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerLastName.updateValueAndValidity();
            this.coBuyerInfoCleared = true;
        }
    }

    private setCustomerData(customerForm: any, prefix): CustomerFormGroup {
        return {
            firstName: customerForm.controls[`${prefix}FirstName`].value,
            middleName: customerForm.controls[`${prefix}MiddleName`].value,
            lastName: customerForm.controls[`${prefix}LastName`].value,
            email: customerForm.controls[`${prefix}Email`].value,
            phone:
                prefix === Assignees.buyer
                    ? this.buyerOriginalPhone
                        ? phoneExporter(this.buyerOriginalPhone)
                        : phoneExporter(customerForm.controls[`${prefix}Phone`].value)
                    : this.coBuyerOriginalPhone
                      ? phoneExporter(this.coBuyerOriginalPhone)
                      : phoneExporter(customerForm.controls[`${prefix}Phone`].value),
            logonId: prefix === Assignees.buyer ? this.buyerLogonId : this.coBuyerLogonId,
            enableMfa: customerForm.controls[`${prefix}EnableMFA`].value,
        };
    }

    public submit(): void {
        if (this.isBuyerDealer || this.isCoBuyerDealer || this.duplicateBuyer) {
            const buyerForm = document.querySelector('.buyer-form-wrapper');

            if (buyerForm) {
                buyerForm.scrollIntoView({
                    behavior: 'smooth',
                });
            }

            return;
        }

        if (!this.isValid()) {
            return;
        }

        this.isLoading = true;
        this.createError = undefined;

        const buyer: CustomerFormGroup = this.setCustomerData(this.newDeal, 'buyer');

        let cobuyer: CustomerFormGroup = null;

        if ((this.newDeal.controls['coBuyerGroup'] as FormGroup).controls.coBuyerEmail.value !== '') {
            cobuyer = this.setCustomerData(this.newDeal.controls['coBuyerGroup'] as FormGroup, 'coBuyer');
        }
        if (this.defaultToMfa) {
            //we want to default new cobuyer and buyers to have mfa enabled, but don't want to override existing users
            if (!this.buyerFound) {
                buyer.enableMfa = true;
            }

            if (!this.coBuyerFound && cobuyer) {
                cobuyer.enableMfa = true;
            }
        }

        let customTemplateGroupDeal = null;

        if (this.selectedTemplateGroup) {
            customTemplateGroupDeal = {
                groupName: this.selectedTemplateGroup.groupName,
                templateGroupId: this.selectedTemplateGroup.templateGroupId,
                dealerId: this.userService.getDealerId(),
            };

            Object.keys(this.dealTypeSelections).forEach((key) => {
                this.dealTypeSelections[key] = false;
            });
        }

        const tradeInInfo = this.newDeal.controls['tradeInGroup'].value;
        let tradeInVehicle: TradeInVehicle | undefined = undefined;

        //ToDo: This is always creating a tradeIn and shouldn't
        //Needs to check for.. && tradeInInfo?.vin !== undefined && tradeInInfo?.vin !== null
        //API (ConvertToDealJacket) will need to be fixed as well to account for null
        if (tradeInInfo && tradeInInfo?.vin !== '') {
            tradeInVehicle = {
                bodyStyle: tradeInInfo?.bodyStyle,
                bodyType: tradeInInfo?.bodyType,
                make: tradeInInfo?.make,
                model: tradeInInfo?.model,
                stockNumber: tradeInInfo?.stockNumber,
                trim: tradeInInfo?.trim,
                vehicleExt: {
                    bodyType: tradeInInfo?.bodyType,
                    exteriorColor: tradeInInfo?.extColor,
                    interiorColor: tradeInInfo?.intColor,
                    priceInternet: tradeInInfo?.priceInternet,
                },
                vin: tradeInInfo.vin,
                year: tradeInInfo?.year,
                payoff: tradeInInfo?.payoff,
                mileage: tradeInInfo?.mileage,
            } as TradeInVehicle;
        }

        // Vehicle has partially or fully been manually entered
        if (this.isVehicleManuallyEntered) {
            const value = this.newDeal.value.vehicleGroup;
            // clear out selectedVehicle and use form data instead
            this.selectedVehicle = {
                dealerId: this.userService.user.dealerId,
                bodyStyle: value.bodyStyle,
                bodyType: value.bodyType,
                year: value.year === '' ? null : value.year,
                make: (value.make || '').trim(),
                model: (value.model || '').trim(),
                trim: value.trim,
                manuallyCreated: true,
                stockNumber: value.stockNumber,
                vehicleExt: {
                    bodyType: value.bodyType,
                    exteriorColor: value.extColor,
                    interiorColor: value.intColor,
                    priceInternet: value.priceInternet,
                    status: this.carStatus?.find((s) => s.value == value.status)?.displayValue,
                    image: this.existingDocumentJacketDeal?.vehicle?.vehicleExt?.image,
                    images: this.existingDocumentJacketDeal?.vehicle?.vehicleExt?.images,
                },
                vin: value.vin,
                mileage: value.mileage,
            };
        }

        if (this.exisitingDocumentJacketDealId) {
            //Caller Handles Custom Errors
            this.dealsService
                .updateDocumentJacketToDealJacket$(
                    this.exisitingDocumentJacketDealId,
                    this.dealTypeSelections,
                    buyer,
                    this.selectedVehicle,
                    this.userService.getDealerId(),
                    cobuyer,
                    this.newDeal.controls.dealerDealNumber.value,
                    customTemplateGroupDeal,
                    tradeInVehicle,
                    this.leadId
                )
                .pipe(
                    catchError((error) => {
                        // handleError will still push the code into submit since we
                        // may have created a deal, but failed to add a document
                        return this.handleError(error);
                    }),
                    takeWhile(() => this.isComponentAlive)
                )
                .subscribe((deal?: Deal) => {
                    buyer.phone = formatPhone(phoneImporter(buyer.phone));
                    this.handleSubscribe({ ...deal, buyer } as any);
                });
        } else {
            //Caller Handles Custom Errors
            this.dealsService
                .createDeal$(
                    this.dealTypeSelections,
                    buyer,
                    this.selectedVehicle,
                    this.userService.getDealerId(),
                    cobuyer,
                    this.newDeal.controls.dealerDealNumber.value,
                    customTemplateGroupDeal,
                    tradeInVehicle,
                    this.leadId
                )
                .pipe(
                    catchError((error) => {
                        // If we receive an error it is assumed that the deal was created
                        // but a document failed to get added
                        return this.handleError(error);
                    }),
                    takeWhile(() => this.isComponentAlive)
                )
                .subscribe((deal?: Deal) => {
                    this.handleSubscribe({ ...deal, buyer } as any);
                });
        }
    }

    private handleSubscribe(deal: Deal) {
        if (deal) {
            this.isLoading = false;

            this.dialog
                .open(DealCreatedModalComponent, {
                    width: '700px',
                    closeOnNavigation: true,
                    data: {
                        ...deal,
                        createError: this.createError,
                    },
                })
                .afterClosed()
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe((val) => {
                    if (deal?.dealId) {
                        if (this.selectedDealType === SelectedDealType.Express) {
                            this.router.navigate(['/dashboard', 'deals', deal.dealId, 'add', 'express']);
                        } else {
                            this.router.navigate(['/dashboard', 'deals', deal.dealId]);
                        }
                    }
                });
        }
    }

    private handleError(error) {
        // 550 is a custom error type. This will redirect to the deals page if we have a dealId
        if (error?.status === 550) {
            let errorMessageDetails;
            try {
                errorMessageDetails = JSON.parse(error.error?.message);
                // Make sure new lines are accounted for
                if (errorMessageDetails?.message) {
                    errorMessageDetails.message = errorMessageDetails?.message?.split('\n').join('<br />');
                }
            } catch (ex) {
                // could not parse JSON message
                errorMessageDetails = error.error?.message;
            }

            this.createError = errorMessageDetails;
            this.isLoading = false;
            return of({ dealId: errorMessageDetails?.dealId });
        } else {
            // Error service is already throwing a modal response
            this.isLoading = false;
            throw error;
        }
    }
}
